import { useCallback, useEffect, useState } from 'react';

/**
 * Hash routing.
 *
 * Hash routes need no server-side history fallback, so the production build drops onto any
 * static host — including a subdirectory — with no rewrite rules. See docs/DEPLOYMENT.md.
 */

export type Route =
  | { readonly name: 'home' }
  | { readonly name: 'play'; readonly level: number }
  | { readonly name: 'levels' }
  | { readonly name: 'settings' }
  | { readonly name: 'about' }
  | { readonly name: 'lab' };

export function parseHash(hash: string): Route {
  const path = hash.replace(/^#/, '').replace(/^\/+/, '');
  const segments = path.split('/').filter((segment) => segment.length > 0);
  const [first, second] = segments;

  switch (first) {
    case undefined:
    case '':
      return { name: 'home' };
    case 'play': {
      const level = Number.parseInt(second ?? '', 10);
      return { name: 'play', level: Number.isInteger(level) && level > 0 ? level : 1 };
    }
    case 'levels':
      return { name: 'levels' };
    case 'settings':
      return { name: 'settings' };
    case 'about':
      return { name: 'about' };
    case 'lab':
      // The laboratory is a development-only surface; in production the route resolves home
      // so a stale bookmark cannot expose developer controls.
      return import.meta.env.DEV ? { name: 'lab' } : { name: 'home' };
    default:
      return { name: 'home' };
  }
}

export function routeToHash(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'play':
      return `#/play/${route.level}`;
    default:
      return `#/${route.name}`;
  }
}

export function useRoute(): [Route, (route: Route) => void] {
  const [route, setRoute] = useState<Route>(() => parseHash(globalThis.location?.hash ?? ''));

  useEffect(() => {
    const onHashChange = (): void => setRoute(parseHash(globalThis.location.hash));
    globalThis.addEventListener('hashchange', onHashChange);
    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = useCallback((next: Route) => {
    const hash = routeToHash(next);
    if (globalThis.location.hash === hash) {
      setRoute(next);
      return;
    }
    globalThis.location.hash = hash;
  }, []);

  return [route, navigate];
}
